import "@expo/metro-runtime";
import { registerRootComponent } from "expo";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Platform, SafeAreaView, StyleSheet, Text, TextInput, View } from "react-native";
import "react-native-get-random-values";
import { Country, LOCALE, Locale } from "@lookiero/sty-psp-locale";
import { Segment } from "@lookiero/sty-psp-segment";
import { Tradename } from "@lookiero/sty-sp-tradename";
import { ExpoRoot } from "./ExpoRoot";
import { fetchConfiguration } from "./devConfiguration";
import { Session, User } from "./infrastructure/ui/shared/types";

const searchParam = (name: string): string | undefined => {
  if (Platform.OS !== "web" || typeof window === "undefined") {
    return undefined;
  }

  return new URLSearchParams(window.location.search).get(name) ?? undefined;
};

const enumValueFromLocation = <T extends string>(values: readonly T[], requested: string | undefined, fallback: T): T =>
  values.find((value) => value === requested) ?? fallback;

const initialTradename: Tradename = enumValueFromLocation(
  Object.values(Tradename),
  searchParam("tradename")?.toUpperCase(),
  Tradename.LOOKIERO,
);
const initialSegment: Segment = enumValueFromLocation(
  Object.values(Segment),
  searchParam("segment")?.toUpperCase(),
  Segment.WOMEN,
);
const locale: Locale = enumValueFromLocation(
  Object.values(Locale),
  searchParam("locale")?.replace("_", "-"),
  Locale.es_ES,
);
const country: Country = LOCALE[locale][1];
const shippingAddress = {
  address: {
    address: "Avenida Antonio Alzaga 15 3-B",
    city: "Santurtzi",
    name: "Janire",
    postCode: "48980",
  },
};
interface ConfiguredCustomer {
  readonly id: string;
  readonly email: string;
  readonly auth_token: string;
}
type ConfiguredCustomers = Partial<Record<Tradename, Partial<Record<Segment, ConfiguredCustomer>>>>;
type AuthTokenOverrides = Partial<Record<Tradename, Partial<Record<Segment, string>>>>;

const fetchConfiguredCustomers = async (): Promise<ConfiguredCustomers> => {
  const configuration = await fetchConfiguration();

  return (configuration.customers ?? {}) as ConfiguredCustomers;
};

const DevRoot = () => {
  const [isRootMounted, setIsRootMounted] = useState(false);
  const [tradename, setTradename] = useState<Tradename>(initialTradename);
  const [segment, setSegment] = useState<Segment>(initialSegment);
  const [authTokenOverrides, setAuthTokenOverrides] = useState<AuthTokenOverrides>({});
  const [configuredCustomers, setConfiguredCustomers] = useState<ConfiguredCustomers>();

  useEffect(() => {
    let isCancelled = false;

    fetchConfiguredCustomers().then((customers) => {
      if (!isCancelled) {
        setConfiguredCustomers(customers);
      }
    });

    return () => {
      isCancelled = true;
    };
  }, []);

  const configuredCustomer = configuredCustomers?.[tradename]?.[segment];
  const authToken = authTokenOverrides[tradename]?.[segment] ?? configuredCustomer?.auth_token ?? "";

  const user: User = useMemo(
    () => ({
      id: configuredCustomer?.id ?? "",
      country,
      segment,
      email: configuredCustomer?.email ?? "",
      shippingAddress,
    }),
    [configuredCustomer, segment],
  );

  const session: Session = useMemo(
    () => ({
      auth: `Bearer ${authToken}`,
      user,
    }),
    [authToken, user],
  );

  const getAuthToken = useCallback(() => Promise.resolve(authToken), [authToken]);

  const handleAuthTokenChange = useCallback(
    (value: string) => {
      setAuthTokenOverrides((previousOverrides) => ({
        ...previousOverrides,
        [tradename]: {
          ...previousOverrides[tradename],
          [segment]: value,
        },
      }));
    },
    [segment, tradename],
  );

  const handleTradenameChange = useCallback((newTradename: Tradename) => setTradename(newTradename), []);

  const handleSetLookiero = useCallback(() => handleTradenameChange(Tradename.LOOKIERO), [handleTradenameChange]);
  const handleSetOutfittery = useCallback(() => handleTradenameChange(Tradename.OUTFITTERY), [handleTradenameChange]);
  const handleSetWomenSegment = useCallback(() => setSegment(Segment.WOMEN), []);
  const handleSetMenSegment = useCallback(() => setSegment(Segment.MEN), []);

  const handleMountRoot = useCallback(() => setIsRootMounted(true), []);

  if (isRootMounted) {
    return <ExpoRoot getAuthToken={getAuthToken} locale={locale} session={session} tradename={tradename} />;
  }

  return (
    <SafeAreaView style={styles.container}>
      <Text style={styles.title}>Look In</Text>

      <View style={styles.section}>
        <Text style={styles.label}>Current user</Text>
        <Text style={styles.detail}>Customer ID: {user.id}</Text>
        <Text style={styles.detail}>Email: {user.email}</Text>
      </View>

      <View style={styles.section}>
        <Text style={styles.label}>Tradename:</Text>
        <View style={styles.row}>
          <Button
            color={tradename === Tradename.LOOKIERO ? "#007AFF" : "#808080"}
            title="LOOKIERO"
            onPress={handleSetLookiero}
          />
          <View style={styles.spacer} />
          <Button
            color={tradename === Tradename.OUTFITTERY ? "#007AFF" : "#808080"}
            title="OUTFITTERY"
            onPress={handleSetOutfittery}
          />
        </View>
      </View>

      <View style={styles.section}>
        <Text style={styles.label}>Segment:</Text>
        <View style={styles.row}>
          <Button
            color={segment === Segment.WOMEN ? "#007AFF" : "#808080"}
            title="WOMEN"
            onPress={handleSetWomenSegment}
          />
          <View style={styles.spacer} />
          <Button color={segment === Segment.MEN ? "#007AFF" : "#808080"} title="MEN" onPress={handleSetMenSegment} />
        </View>
      </View>

      <View style={styles.section}>
        <Text style={styles.label}>Auth Token:</Text>
        <TextInput
          numberOfLines={3}
          placeholder="Enter Auth Token"
          style={[styles.input, styles.textArea]}
          value={authToken}
          multiline
          onChangeText={handleAuthTokenChange}
        />
      </View>

      <View style={styles.buttonContainer}>
        <Button disabled={!authToken} testID="go-button" title="GO!" onPress={handleMountRoot} />
      </View>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  buttonContainer: {
    marginVertical: 10,
    width: "80%",
  },
  container: {
    alignItems: "center",
    backgroundColor: "#fff",
    flex: 1,
    justifyContent: "center",
  },
  detail: {
    fontSize: 14,
    marginTop: 4,
  },
  input: {
    borderColor: "gray",
    borderWidth: 1,
    height: 40,
    marginTop: 5,
    paddingHorizontal: 10,
    width: "100%",
  },
  label: {
    fontSize: 16,
    fontWeight: "bold",
  },
  row: {
    flexDirection: "row",
    marginTop: 5,
  },
  section: {
    marginBottom: 20,
    width: "80%",
  },
  spacer: {
    width: 20,
  },
  textArea: {
    height: 80,
    textAlignVertical: "top",
  },
  title: {
    fontSize: 20,
    fontWeight: "bold",
    marginBottom: 20,
  },
});

registerRootComponent(DevRoot);
